import React, {useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import {axiosRun} from "../utils/axiosWrap";
import {config} from "../config";

const defaultParams = {
    UniversityId: 151,
    QualificationGroupId: 2,
    HistoryFilterId: 1,
    CourseId: 4,
    FacultyId: "1452"
};

const fields = [
    {name: "UniversityId", label: "UniversityId", disabled: true},
    {name: "HistoryFilterId", label: "HistoryFilterId"},
    {name: "CourseId", label: "Курс"}
];

const qualificationGroups = [
    {id: 5, name: "Кваліфікований робітник"},
    {id: 9, name: "Фаховий молодший бакалавр"},
    {id: 4, name: "Молодший спеціаліст"},
    {id: 6, name: "Молодший бакалавр"},
    {id: 1, name: "Бакалавр"},
    {id: 3, name: "Спеціаліст"},
    {id: 2, name: "Магістр"},
    {id: 7, name: "Доктор філософії"},
    {id: 10, name: "Доктор мистецтва"},
    {id: 8, name: "Доктор наук"}
];

const historyFilterStatuses = [
    {value: "1", label: "навчаються", statuses: "1, 10, 11, 12, 13, 14, 15, 17, 20, 21, 25, 30, 50, 61, 64, 70, 80, 81, 90"},
    {value: "2", label: "призупинено", statuses: "16, 60, 62, 63, 65"},
    {value: "3", label: "відраховані", statuses: "110"},
    {value: "4", label: "завершили навчання", statuses: "100, 101"},
    {value: "-1", label: "фільтр не застосовується", statuses: "заяви зі всіма статусами"}
];

const queryParamNames = [...fields.map(({name}) => name), "QualificationGroupId", "FacultyId"];

const faculties = [
    {name: "Факультет економіки і менеджменту", alternativeName: "1408"},
    {name: "Факультет ветеринарної медицини", alternativeName: "1448"},
    {name: "Факультет харчових технологій", alternativeName: "1456"},
    {name: "Факультет будівництва та транспорту", alternativeName: "1431"},
    {name: "Факультет агротехнологій та природокористування", alternativeName: "1400"},
    {name: "Інженерно-технологічний факультет", alternativeName: "1404"},
    {name: "Біолого-технологічний факультет", alternativeName: "1452"},
    {name: "Юридичний факультет", alternativeName: "1416"}
];

export const EdeboSync = () => {
    const [params, setParams] = useState(defaultParams);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [syncResult, setSyncResult] = useState(null);

    const syncUrl = useMemo(() => {
        const queryParams = new URLSearchParams();

        queryParamNames.forEach((name) => {
            queryParams.set(name, params[name]);
        });

        return `${config.apiLinks.syncEdebo}?${queryParams.toString()}`;
    }, [params]);

    const changeParam = (name, value) => {
        setParams((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const startSync = async () => {
        setIsLoading(true);
        setError(null);
        setSyncResult(null);

        try {
            const result = await axiosRun(syncUrl, "GET");
            setSyncResult(result);
        } catch (syncError) {
            setError(syncError.data?.message || syncError.data?.error || "Помилка синхронізації ЄДЕБО");
        } finally {
            setIsLoading(false);
        }
    };

    const hasEmptyParams = queryParamNames.some((name) => String(params[name]).trim() === "");

    return (
        <Paper sx={{p: 3, minHeight: "85vh"}}>
            <Typography variant="h4" component="h1" gutterBottom>
                Синхронізація ЄДЕБО
            </Typography>

            <Grid container spacing={2} sx={{mt: 1, maxWidth: 900}}>
                {fields.map(({name, label, disabled, helperText}) => (
                    <Grid item xs={12} sm={6} md={4} key={name}>
                        <TextField
                            fullWidth
                            required
                            disabled={disabled}
                            type="number"
                            label={label}
                            value={params[name]}
                            onChange={(event) => changeParam(name, event.target.value)}
                            inputProps={{min: 0}}
                            helperText={helperText}
                        />
                    </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth required>
                        <InputLabel id="edebo-qualification-group-label">
                            Код освітнього ступеня (рівня) ( QualificationGroupId )
                        </InputLabel>
                        <Select
                            labelId="edebo-qualification-group-label"
                            label="Код освітнього ступеня (QualificationGroupId)"
                            value={params.QualificationGroupId}
                            onChange={(event) => changeParam("QualificationGroupId", event.target.value)}
                        >
                            {qualificationGroups.map((qualificationGroup) => (
                                <MenuItem value={qualificationGroup.id} key={qualificationGroup.id}>
                                    {qualificationGroup.name} ({qualificationGroup.id})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth required>
                        <InputLabel id="edebo-faculty-label">Факультет</InputLabel>
                        <Select
                            labelId="edebo-faculty-label"
                            label="Факультет"
                            value={params.FacultyId}
                            onChange={(event) => changeParam("FacultyId", event.target.value)}
                        >
                            {faculties.map((faculty) => (
                                <MenuItem value={faculty.alternativeName} key={faculty.alternativeName}>
                                    {faculty.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Paper variant="outlined" sx={{mt: 3, p: 2, maxWidth: 900, backgroundColor: "#fff"}}>
                <Typography variant="subtitle1" gutterBottom>
                    HistoryFilterId
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Код фільтру по статусам навчання.
                </Typography>
                <Box sx={{mt: 1, display: "grid", gap: 1}}>
                    {historyFilterStatuses.map((item) => (
                        <Box
                            key={item.value}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "36px 1fr",
                                gap: 1,
                                alignItems: "baseline"
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                {item.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {item.label}: {item.statuses}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            <Box sx={{mt: 3, display: "flex", alignItems: "center", gap: 2}}>
                <Button
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                    disabled={isLoading || hasEmptyParams}
                    onClick={startSync}
                >
                    Розпочати
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{mt: 3}}>
                    {error}
                </Alert>
            )}

            {syncResult && (
                <Box sx={{mt: 4}}>
                    <Typography variant="h6" gutterBottom>
                        Результат синхронізації
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="body2">Import hash: {syncResult.importHash}</Typography>
                                <Typography variant="body2">Сторінок завантажено: {syncResult.pagesLoaded}</Typography>
                                <Typography variant="body2">Усього записів: {syncResult.total}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="body2">Users found: {syncResult.usersSync?.found}</Typography>
                                <Typography variant="body2">Users created: {syncResult.usersSync?.created}</Typography>
                                <Typography variant="body2">Users updated: {syncResult.usersSync?.updated}</Typography>
                                <Typography variant="body2">Errors: {syncResult.usersSync?.errors?.length || 0}</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Paper>
    );
};
